import { useMemo, useState } from 'react';

import { Box, Tooltip, Typography, Chip, Divider, Stack } from '@mui/material';
import { Cloud, Business, Person, Check, Close, HelpOutline } from '@mui/icons-material';
import ClickAwayListener from '@mui/material/ClickAwayListener';

import awsconfig from 'awsConfig';

import { determineEnvironment } from 'app/pages/onboard/utils';
import { useAppSelector } from 'app/store';
import { selectAuth } from 'app/store/authSlice';
import { selectUserCompanies, selectUserPaidolUsers } from 'app/store/userCompaniesSlice';
import { sentryMask } from 'sentryReplayPrivacy';

interface EnvironmentStripeProps {
  children?: React.ReactNode;
}
interface InfoSectionProps {
  icon: React.ReactNode;
  title: string;
  items: Array<{ label: string; value: React.ReactNode }>;
  bgcolor: string;
}

// Color-coded theme constants
const SECTION_COLORS = {
  aws: { icon: '#FF9800', bg: '#FFF3E0' },
  company: { icon: '#4CAF50', bg: '#E8F5E9' },
  user: { icon: '#E91E63', bg: '#FCE4EC' },
  chip: { success: '#4CAF50', error: '#f44336' },
} as const;

function InfoSection({ icon, title, items, bgcolor }: InfoSectionProps): JSX.Element {
  return (
    <Box sx={{ mb: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          mb: 1.5,
          bgcolor,
          borderRadius: 1,
          p: 1,
          px: 1.5,
        }}
      >
        {icon}
        <Typography variant="subtitle2" fontWeight="bold">
          {title}
        </Typography>
      </Stack>
      <Box sx={{ pl: 2 }}>
        {items.map(({ label, value }, index) => (
          <Typography key={label} variant="body2" sx={{ mb: index < items.length - 1 ? 0.5 : 0 }}>
            <strong>{label}:</strong> {value}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

// Helper function to truncate strings
const truncateString = (str: string | undefined, length = 20): string => {
  if (!str) return 'N/A';
  return str.length > length ? `${str.substring(0, length)}...` : str;
};

// Helper function to format role name
const formatRoleName = (role?: string | null): string => {
  if (!role) return 'N/A';
  return role
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

// Helper to determine authentication status properties
type AuthStatus = 'unknown' | 'authenticated' | 'not-authenticated';

const getAuthStatusConfig = (isAuthStateUnknown: boolean, user: any) => {
  let status: AuthStatus;

  if (isAuthStateUnknown) {
    status = 'unknown';
  } else if (user) {
    status = 'authenticated';
  } else {
    status = 'not-authenticated';
  }

  const configs = {
    unknown: {
      icon: <HelpOutline />,
      label: 'Unknown',
      color: 'warning' as const,
    },
    authenticated: {
      icon: <Check />,
      label: 'Authenticated',
      color: 'success' as const,
    },
    'not-authenticated': {
      icon: <Close />,
      label: 'Not Authenticated',
      color: 'error' as const,
    },
  };

  return configs[status];
};

function EnvironmentStripe({ children }: EnvironmentStripeProps): JSX.Element {
  const environment = determineEnvironment();
  const environmentName = environment !== 'production' && environment;

  // Redux selectors
  const { selectedPaidolId, selectedPaidol, userRole } = useAppSelector(selectUserCompanies);
  const paidolUsers = useAppSelector(selectUserPaidolUsers);
  const { username, user, isAuthStateUnknown } = useAppSelector(selectAuth);

  const [openTooltip, setOpenTooltip] = useState(false);

  // Find the current PaidolUser based on selectedPaidolId and current user
  const currentPaidolUser = useMemo(() => {
    if (!selectedPaidolId || !user?.sub || !paidolUsers || paidolUsers.length === 0) {
      return null;
    }
    // PaidolUser has user_id field that should match the user's sub
    return (
      paidolUsers.find((pu: any) => pu.paidol_id === selectedPaidolId && pu.user_id === user.sub) || null
    );
  }, [selectedPaidolId, user?.sub, paidolUsers]);

  // Determine if user is a support user (has access to company but no PaidolUser record)
  const isSupportUser = useMemo(() => {
    return Boolean(
      selectedPaidolId && user?.sub && paidolUsers && paidolUsers.length > 0 && !currentPaidolUser
    );
  }, [selectedPaidolId, user?.sub, paidolUsers, currentPaidolUser]);

  // Memoize tooltip content for performance
  const tooltipContent = useMemo(() => {
    if (environmentName !== 'development') return null;

    // Get auth status configuration
    const authStatusConfig = getAuthStatusConfig(isAuthStateUnknown, user);

    return (
      <Box sx={{ p: 2, maxWidth: 400 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
          Environment Details
        </Typography>

        {/* AWS Configuration Section */}
        <InfoSection
          icon={<Cloud sx={{ color: SECTION_COLORS.aws.icon }} />}
          title="AWS Configuration"
          bgcolor={SECTION_COLORS.aws.bg}
          items={[
            { label: 'Environment', value: environmentName.toUpperCase() },
            { label: 'Region', value: awsconfig.aws_project_region || 'N/A' },
            { label: 'User Pool', value: truncateString(awsconfig.aws_user_pools_id, 35) },
            { label: 'API', value: truncateString(awsconfig.aws_appsync_graphqlEndpoint, 35) },
          ]}
        />

        <Divider sx={{ my: 2 }} />

        {/* Company Context Section */}
        <InfoSection
          icon={<Business sx={{ color: SECTION_COLORS.company.icon }} />}
          title="Paidol"
          bgcolor={SECTION_COLORS.company.bg}
          items={[
            { label: 'Paidol ID', value: selectedPaidolId || 'Not selected' },
            {
              label: 'PaidolUser ID',
              value: currentPaidolUser?.id
                ? truncateString(currentPaidolUser.id, 30)
                : isSupportUser
                ? 'N/A (Support User)'
                : 'N/A',
            },
            { label: 'Company', value: selectedPaidol?.name || 'N/A' },
            {
              label: 'Role',
              value: userRole ? (
                <Chip label={formatRoleName(userRole)} size="small" color="primary" variant="outlined" />
              ) : (
                'N/A'
              ),
            },
          ]}
        />

        <Divider sx={{ my: 2 }} />

        {/* User Session Section */}
        <InfoSection
          icon={<Person sx={{ color: SECTION_COLORS.user.icon }} />}
          title="User"
          bgcolor={SECTION_COLORS.user.bg}
          items={[
            // { label: 'User ID', value: truncateString(user?.sub, 30) },
            { label: 'User ID', value: user?.sub || 'N/A' },
            { label: 'Username', value: <span {...sentryMask}>{username || 'N/A'}</span> },
            { label: 'Email', value: <span {...sentryMask}>{user?.email || 'N/A'}</span> },
            {
              label: 'Status',
              value: (
                <Chip
                  icon={authStatusConfig.icon}
                  label={authStatusConfig.label}
                  size="small"
                  color={authStatusConfig.color}
                  variant="outlined"
                />
              ),
            },
          ]}
        />
      </Box>
    );
  }, [
    environmentName,
    selectedPaidolId,
    selectedPaidol,
    userRole,
    username,
    user,
    isAuthStateUnknown,
    currentPaidolUser,
    isSupportUser,
  ]);

  // Render the banner content
  const bannerContent = (
    <Box
      onClick={() => setOpenTooltip(!openTooltip)}
      role="button"
      aria-label="Show environment details for developers"
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '16px',
        textTransform: 'uppercase',
        padding: '5px 0',
        color: '#FFF',
        backgroundColor: '#EB5757',
        zIndex: 1101,
        position: 'fixed',
        width: '100%',
        top: 0,
        left: 0,
        ...(environmentName === 'development' && {
          cursor: 'help',
          '&:hover': {
            backgroundColor: '#D84343',
          },
        }),
      }}
    >
      {environmentName} environment
    </Box>
  );

  return (
    <Box
      sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        maxWidth: '100vw',
        overflow: 'hidden',
      }}
    >
      {environmentName && (
        <>
          {environmentName === 'development' && tooltipContent ? (
            <ClickAwayListener onClickAway={() => setOpenTooltip(false)}>
              <Tooltip
                title={tooltipContent}
                placement="bottom"
                disableHoverListener
                open={openTooltip}
                onClose={() => setOpenTooltip(!openTooltip)}
                componentsProps={{
                  tooltip: {
                    id: 'env-stripe-tooltip',
                    sx: {
                      bgcolor: 'background.paper',
                      color: 'text.primary',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      maxWidth: 'none',
                      '& .MuiTooltip-arrow': {
                        color: 'background.paper',
                        '&::before': {
                          border: '1px solid',
                          borderColor: 'divider',
                        },
                      },
                    },
                  },
                }}
              >
                {bannerContent}
              </Tooltip>
            </ClickAwayListener>
          ) : (
            bannerContent
          )}
        </>
      )}
      {!environmentName && (
        <Box
          sx={{
            padding: '16px 0',
          }}
        />
      )}
      {children}
    </Box>
  );
}

export default EnvironmentStripe;
